import React, { createContext, forwardRef, useContext, useEffect, useState } from 'react';
import { ChevronDownIcon, MenuAlt2Icon, MoonIcon, SearchIcon, SelectorIcon, SunIcon } from '@heroicons/react/outline';
import useViewport from '../hooks/useViewport';
import Avatar from './Avatar';
import Badge from './Badge';
import Tooltip from './Tooltip';
import Button from './Button';
import Menu from './Menu';
import SectionLabel from './SectionLabel';
import SlidePanel from './SlidePanel';
import HouseholdHeader from './HouseholdHeader';
import PageLayout from './PageLayout';
import Tabs from './Tabs';
import { cn } from './cn';
import { ABUNDO_APP_LOGO_URL, ABUNDO_MARK_URL, ABUNDO_WORDMARK_URL } from './brandAssets';
import { applyBunTheme, getBunTheme, setBunTheme } from './theme';

const NavigationContext = createContext(null);

const NavigationItem = forwardRef(function NavigationItem({ icon: Icon, label, shortcut, active, variant = 'secondary', count, danger, children, className, ...props }, ref) {
  const button = (
    <button ref={ref} type="button" aria-current={active ? 'page' : undefined}
      className={cn('flex min-h-[46px] sm:min-h-[38px] w-full items-center gap-3 sm:gap-[10px] rounded-bun-lg border-0 px-3 sm:px-[9px] py-2 text-left font-instrument text-bun-heading sm:text-bun-body cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue disabled:cursor-not-allowed disabled:opacity-50',
        active ? cn('font-semibold', variant === 'primary' ? 'bg-bun-ink text-bun-on-ink' : 'bg-bun-mist-deep text-bun-slate') : danger ? 'bg-transparent text-bun-danger hover:bg-bun-danger-wash font-book' : 'bg-transparent text-bun-slate hover:bg-bun-mist hover:text-bun-text font-book', className)} {...props}>
      {Icon && <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count > 0 && <Badge variant="count" tone="danger">{count}</Badge>}
      {children}
    </button>
  );
  return shortcut ? <Tooltip portal
    label={Array.isArray(shortcut) ? shortcut.map(key => <span key={key} className="block">Hotkey: {key}</span>) : `Hotkey: ${shortcut}`}
    maxHeight={Array.isArray(shortcut) ? 256 : undefined} anchorClassName="w-full">{button}</Tooltip> : button;
});

function Toggle() {
  const context = useContext(NavigationContext);
  if (!context) return null;
  return <Button variant="secondary" iconOnly aria-label="Open advisor navigation" aria-haspopup="dialog" aria-expanded={context.open} onClick={() => context.setOpen(true)}><MenuAlt2Icon className="h-[18px] w-[18px]" aria-hidden="true" /></Button>;
}

function Brand({ name: brandName, logo, markOnly = false, size = 'md', onClick, destination = 'home' }) {
  const name = brandName === 'Abundo Wealth' ? 'Abundo' : brandName;
  const source = name === 'Abundo' && !markOnly && (!logo || logo === ABUNDO_MARK_URL)
    ? ABUNDO_WORDMARK_URL
    : logo || (name === 'Abundo' ? ABUNDO_MARK_URL : null);
  const isWordmark = source === ABUNDO_WORDMARK_URL && !markOnly;
  const markSize = size === 'lg' ? 'h-10 w-10' : 'h-8 w-8';
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [source]);
  const content = <div className="flex min-w-0 items-center gap-[10px]">
    {source && !failed ? <img src={source} alt={markOnly || isWordmark ? name : ''} onError={() => setFailed(true)} className={isWordmark ? 'h-12 w-auto max-w-full object-contain' : cn(markSize, 'shrink-0 object-contain')} /> : <div aria-label={markOnly ? name : undefined} className={cn(markSize, 'flex shrink-0 items-center justify-center rounded-bun bg-bun-logo text-white text-bun-heading font-semibold')}>{name.slice(0, 1)}</div>}
    {!markOnly && (!isWordmark || failed) && <span className={cn('min-w-0 whitespace-normal break-words text-left font-semibold text-bun-text', size === 'lg' ? 'text-xl' : 'text-bun-heading')}>{name}</span>}
  </div>;
  return onClick ? <Button variant="ghost" iconOnly={markOnly} className={cn(markOnly || isWordmark || size === 'lg' ? 'max-w-full p-0' : 'max-w-full', markOnly && size === 'lg' && markSize)} aria-label={`${name} ${destination}`} onClick={onClick}>{content}</Button> : content;
}

function getSidebarLogo(brand, logo) {
  return (brand === 'Abundo' || brand === 'Abundo Wealth') && (!logo || logo === ABUNDO_MARK_URL) ? ABUNDO_APP_LOGO_URL : logo;
}

function Sidebar({
  mobile: isMobile = false, brand = 'Abundo', logo, onBrandClick, items = [], moreItems = [], groups = [], actions = [], account = {}, accountActions = [], accountTools, onSelect, onSearch, searchShortcut = 'Ctrl+K or Ctrl+Space',
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState({});
  const [theme, setTheme] = useState(getBunTheme);
  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => setTheme(root.getAttribute('data-bun-theme') === 'dark' ? 'dark' : 'light'));
    observer.observe(root, { attributes: true, attributeFilter: ['data-bun-theme'] });
    return () => observer.disconnect();
  }, []);
  const select = (item) => onSelect ? onSelect(item) : item.onClick?.();
  const sidebarLogo = getSidebarLogo(brand, logo);

  const identity = <><Avatar name={account.name || 'Advisor'} size="md" tone="blue" /><span className="min-w-0 flex-1"><span className="block truncate text-bun-body font-strong text-bun-text">{account.name || 'Advisor'}</span><span className="block truncate text-bun-meta text-bun-stone">{isMobile ? account.email : 'Advisor'}</span></span></>;
  return <div className={isMobile ? "flex min-h-full flex-col" : "flex min-h-0 flex-1 flex-col"}>
    {!isMobile && <div className="mb-1 flex justify-start px-[3px]"><Brand name={brand} logo={sidebarLogo} size="lg" onClick={onBrandClick} destination="dashboard" /></div>}
    <div className={isMobile ? "shrink-0" : "min-h-0 flex-1 overflow-y-auto"}>
    <NavigationItem icon={SearchIcon} label={isMobile ? 'Search Households' : 'Search'} aria-label="Search Households" shortcut={isMobile ? undefined : searchShortcut} onClick={onSearch} className={isMobile ? 'border border-bun-line bg-bun-mist' : undefined} />
    <div className="px-3 sm:px-[9px] pt-4 pb-1 text-bun-label font-semibold uppercase tracking-[0.08em] text-bun-faint">Navigate</div>
    <nav aria-label="Advisor navigation" className="flex flex-col gap-1 sm:gap-px">
      {items.map((item) => <NavigationItem key={item.id} {...item} onClick={() => select(item)} />)}
      {moreItems.length > 0 && <>
        <NavigationItem icon={ChevronDownIcon} label="More" active={moreItems.some((item) => item.active)} aria-expanded={moreOpen} onClick={() => setMoreOpen(!moreOpen)} />
        {moreOpen && <nav aria-label="More destinations">{moreItems.map((item) => <NavigationItem key={item.id} {...item} onClick={() => select(item)} />)}</nav>}
      </>}
      {groups.map(group => <React.Fragment key={group.id}>
        <NavigationItem icon={ChevronDownIcon} label={group.label} active={group.items.some(item => item.active)} aria-expanded={!!groupsOpen[group.id]} onClick={() => setGroupsOpen(open => ({ ...open, [group.id]: !open[group.id] }))} />
        {groupsOpen[group.id] && <nav aria-label={group.label} className="flex flex-col gap-1 sm:gap-px">{group.items.map(item => <NavigationItem key={item.id} {...item} onClick={() => select(item)} />)}</nav>}
      </React.Fragment>)}
    </nav>
    <div className="px-3 sm:px-[9px] pt-4 pb-1 text-bun-label font-semibold uppercase tracking-[0.08em] text-bun-faint">Do</div>
    <nav aria-label="Quick actions" className="flex flex-col gap-1 sm:gap-px">
      {actions.map((item) => <NavigationItem key={item.id} {...item} shortcut={isMobile ? undefined : item.shortcut} onClick={() => select(item)} />)}
    </nav>
    </div>
    <div className="mt-auto shrink-0 pt-6">
      {accountTools && <div className="mb-2">{accountTools}</div>}
      {isMobile ? <div className="border-t border-bun-line-soft pt-3">
        <div className="flex items-center gap-3 px-3 pb-3">{identity}</div>
        <nav aria-label="Account">{accountActions.map((item) => <NavigationItem key={item.id} {...item} onClick={() => select(item)} />)}</nav>
      </div> : <Menu align="left" side="top" width={240} className="w-full" button={<Button variant="ghost" className="w-full justify-start gap-[10px] px-[5px] py-2 text-left" aria-label="Open account menu">{identity}<SelectorIcon className="h-3 w-3 shrink-0 text-bun-faint" /></Button>}>
        {account.email && <div className="truncate px-[10px] py-2 text-bun-meta text-bun-stone">{account.email}</div>}
        {accountActions.map((item) => <Menu.Item key={item.id} icon={item.icon} danger={item.danger} disabled={item.disabled} onClick={() => select(item)}>{item.label}</Menu.Item>)}
      </Menu>}
      <NavigationItem icon={theme === 'dark' ? SunIcon : MoonIcon} label={theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
        onClick={() => setTheme(setBunTheme(document.documentElement.getAttribute('data-bun-theme') === 'dark' ? 'light' : 'dark'))} />
    </div>
  </div>;
}

/** Expanded desktop rail and mobile drawer, with a shared menu trigger for page headers. */
export default function AppNavigation({ mobileHeader = true, mobileTitle, fillViewport = false, focusMode = false, onExitFocus, rightInset = 0, children, ...navigation }) {
  const { isMobile, isTablet } = useViewport();
  const compact = isMobile || isTablet;
  const [open, setOpen] = useState(false);
  useEffect(() => { applyBunTheme(); }, []);
  useEffect(() => { if (!compact) setOpen(false); }, [compact]);
  const brand = navigation.brand || "Abundo";
  const goDashboard = navigation.onBrandClick ? () => { setOpen(false); navigation.onBrandClick(); } : undefined;
  const content = <Sidebar {...navigation} mobile={compact} onSelect={(item) => { setOpen(false); item.onClick?.(); }} onSearch={() => { setOpen(false); navigation.onSearch?.(); }} />;
  return <NavigationContext.Provider value={{ open, setOpen }}>
    <div className={cn('bun font-instrument text-bun-text', fillViewport ? 'h-[100dvh] overflow-hidden' : 'min-h-screen')}>
      {!focusMode && !compact && <aside data-bun-sidebar aria-label="Advisor sidebar" className="fixed inset-y-0 left-0 z-40 flex w-[192px] flex-col border-r border-bun-line bg-bun-surface px-2 pt-2 pb-[14px]">
        {content}
      </aside>}
      {compact && <SlidePanel open={open} onClose={() => setOpen(false)} side="left" title={brand} leading={<Brand name={brand} logo={getSidebarLogo(brand, navigation.logo)} size="lg" onClick={goDashboard} destination="dashboard" />} hideTitle width={296} closeLabel="Close advisor navigation" closeButtonVariant="ghost" variant="navigation">
        {content}
      </SlidePanel>}
      <div className={cn('min-w-0', fillViewport && 'flex h-full min-h-0 flex-col', !focusMode && 'lg:pl-[192px]')} style={rightInset ? { marginRight: rightInset } : undefined}>
        {focusMode ? <div className="flex shrink-0 justify-end p-3"><Button variant="secondary" onClick={onExitFocus}>Exit fullscreen</Button></div> : compact && mobileHeader && <header className="flex shrink-0 items-center gap-[10px] px-4 py-3"><Toggle /><span className="min-w-0 truncate text-bun-heading font-semibold">{mobileTitle || brand}</span></header>}
        {children}
      </div>
    </div>
  </NavigationContext.Provider>;
}

AppNavigation.Toggle = Toggle;

AppNavigation.Sidebar = Sidebar;

/** Top navigation on desktop and a compact identity / left drawer on mobile. */
function Portal({ brand = 'Abundo', logo, onBrandClick, title, meta, avatars, mobileAvatars, headerActions,
  items = [], moreItems = [], moreGroups = [], accountActions = [], rightInset = 0, useContainer = true, children }) {
  const { isMobile, isTablet } = useViewport();
  const compact = isMobile || isTablet;
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => { applyBunTheme(); }, []);
  useEffect(() => { if (!compact) setOpen(false); }, [compact]);
  const select = (item) => { setOpen(false); item.onClick?.(); };
  const active = items.find((item) => item.active)?.id;
  const groups = [{ id: 'more', items: moreItems }, ...moreGroups].filter((group) => group.items.length > 0);
  const moreActive = groups.some((group) => group.items.some((item) => item.active));
  const compactAvatars = mobileAvatars || avatars;
  const goHome = onBrandClick ? () => { setOpen(false); onBrandClick(); } : undefined;
  const branding = <Brand name={brand} logo={getSidebarLogo(brand, logo)} size="lg" onClick={goHome} />;
  return <div className="bun min-h-screen font-instrument text-bun-text">
    <div className="min-h-screen bg-bun-canvas" style={rightInset ? { marginRight: rightInset } : undefined}>
      {compact ? <header className="sticky top-0 z-30 flex flex-wrap items-center gap-[10px] border-b border-bun-line bg-bun-surface px-3 py-2">
        <Button variant="secondary" iconOnly className="sm:min-h-11 sm:min-w-11" aria-label="Open client navigation" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}><MenuAlt2Icon className="h-[18px] w-[18px]" aria-hidden="true" /></Button>
        {compactAvatars}
        <div className="min-w-0 flex-1 truncate text-bun-heading font-semibold">{title}</div>
        {headerActions && <div className="flex w-full flex-wrap items-center justify-end gap-[10px] sm:w-auto">{headerActions}</div>}
      </header> : <header className="box-border w-full px-11 pt-7">
        <HouseholdHeader title={title} meta={meta} avatars={<div className="flex items-center gap-[18px]">{branding}{avatars}</div>}>
          {headerActions}
          {accountActions.map(({ id, label, icon: Icon, onClick }) => <Button key={id} variant="secondary" onClick={onClick} className="whitespace-nowrap">{Icon && <Icon className="h-[14px] w-[14px]" aria-hidden="true" />}{label}</Button>)}
        </HouseholdHeader>
        <div className="mt-[18px] flex items-end gap-3 border-b border-bun-line" data-tooltip="false">
          <Tabs tabs={items} active={active} ariaLabel="Client sections" className="min-w-0 flex-1 border-b-0" onChange={(id) => select(items.find((item) => item.id === id))} />
          {groups.length > 0 && <Menu button={<Button variant="ghost" selected={moreActive} aria-label="More client navigation" className="shrink-0">More<ChevronDownIcon className="h-3 w-3" aria-hidden="true" /></Button>}>
            {groups.map((group, index) => <React.Fragment key={group.id}>
              {index > 0 && group.label && <Menu.Divider />}
              <div role="group" aria-label={group.label}>
                {group.label && <Menu.Section><SectionLabel as="div">{group.label}</SectionLabel></Menu.Section>}
                {group.items.map((item) => <Menu.Item key={item.id} onClick={() => select(item)}>{item.label}</Menu.Item>)}
              </div>
            </React.Fragment>)}
          </Menu>}
        </div>
      </header>}
      <div className="box-border w-full">
        {useContainer ? <PageLayout><PageLayout.Frame>
          <PageLayout.HeaderRow variant="client" />
          {children}
        </PageLayout.Frame></PageLayout> : children}
      </div>
    </div>
    {compact && <SlidePanel open={open} onClose={() => setOpen(false)} side="left" variant="navigation" title={`${brand} navigation`} leading={branding} hideTitle width={296} closeLabel="Close client navigation" closeButtonVariant="ghost">
      <nav aria-label="Client sections" className="space-y-1" data-tooltip="false">
        {items.map((item) => <NavigationItem key={item.id} {...item} variant="primary" className="sm:min-h-[46px]" onClick={() => select(item)} />)}
        {groups.length > 0 && <>
          <NavigationItem label="More" active={moreActive} variant="primary" icon={ChevronDownIcon} aria-expanded={moreOpen} className="sm:min-h-[46px]" onClick={() => setMoreOpen(!moreOpen)} />
          {moreOpen && <nav aria-label="More client destinations">{groups.map((group) => <div key={group.id} role="group" aria-label={group.label}>
            {group.label && <SectionLabel as="div" className="px-3 pt-4 pb-1">{group.label}</SectionLabel>}
            {group.items.map((item) => <NavigationItem key={item.id} {...item} variant="primary" className="sm:min-h-[46px]" onClick={() => select(item)} />)}
          </div>)}</nav>}
        </>}
      </nav>
      {accountActions.length > 0 && <div className="mt-auto border-t border-bun-line-soft pt-3">
        <nav aria-label="Account">{accountActions.map((item) => <NavigationItem key={item.id} {...item} variant="primary" className="sm:min-h-[46px]" onClick={() => select(item)} />)}</nav>
      </div>}
    </SlidePanel>}
  </div>;
}

AppNavigation.Portal = Portal;
